import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { API } from '../../api';
import { getPotentialTalentArray, getReport, curvePickForReply } from '../../Jacques';
import {
    getFuturePotentialCategory,
    getPositionMatch,
    PROMOTE_NOW,
    PROMOTE_SOON,
    KEEP_ROLE,
} from '../../utils/employees';
import { DEFAULT_POTENTIAL_RULESET } from '../../utils/potentialDSL';
import { withLanguage } from '../common/Texts';
import { getText } from '../common/MultiLanguageText';
import { toTitleCase } from '../../utils/toTitleCase';

const NUMBER_TO_ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI'];

function capacityToText(level, sublevel, tr) {
    if (level === null || level === undefined || level === '') return '';
    return `${tr(`capacity_${level}${sublevel}`)} (${NUMBER_TO_ROMAN[level]} ${sublevel})`;
}

function patchReport(companyName, report) {
    if (!companyName?.toLowerCase().includes('ternium')) return report;
    return report
        .replaceAll('C Level corporativos', 'C-Level President')
        .replaceAll('C-Level corporativo', 'C-Level President')
        .replaceAll('C-Level roles', 'C-Level President')
        .replaceAll('Presidente o CEO pais', 'C-Level Funcional')
        .replaceAll('Presidente ou CEO no país', 'C-Level Funcional')
        .replaceAll('Country President or CEO positions', 'C-Level Funcional positions');
}

const VALID_LEVELS = new Set(['1C','1B','1A','2C','2B','2A','3C','3B','3A','4C','4B','4A','5C','5B','5A','6C','6B','6A','7C','7B','7A']);

function resolveLevel(type, level_label, sublevel_label, level_calc, sublevel_calc) {
    if (type === 'l') return { level: level_label, sublevel: sublevel_label };
    if (level_calc == null && level_label != null) return { level: level_label, sublevel: sublevel_label };
    return { level: level_calc, sublevel: sublevel_calc };
}

function isValidLevel(level, sublevel) {
    if (level == null || sublevel == null) return false;
    return VALID_LEVELS.has(String(level) + String(sublevel).toUpperCase());
}

export default function useReportData(id, type) {
    const language = useSelector((state) => state.user.language);
    // El estado se escribe solo desde los callbacks del fetch: `loading` se deriva
    // comparando la petición pedida (requestKey) con la ya resuelta (result.key).
    const [result, setResult] = useState({
        key: null, data: null, error: null, pending: false,
    });

    const requestKey = `${id}|${type}|${language}`;

    useEffect(() => {
        if (!id) return undefined;
        const controller = new AbortController();
        API.get(`/reply/${id}/`, { signal: controller.signal }).then((response) => {
            const tr = withLanguage(language);
            const reply = response.data;

            if (!reply || reply.invalid) {
                setResult({ key: requestKey, data: null, error: null, pending: true });
                return;
            }

            const company_data = reply.questionnaire.company;
            const {
                name, age, position, department,
                level_label, sublevel_label,
                level_calc, sublevel_calc,
                created_date, age_at_reply,
            } = reply;

            const positions = company_data.positions;
            const departments = company_data.departments;

            const posicion = positions.find((p) => p.id == position);
            const areafunc = departments.find((d) => d.id == department);

            const { level, sublevel } = resolveLevel(
                type, level_label, sublevel_label, level_calc, sublevel_calc,
            );

            const hasLevel = isValidLevel(level, sublevel);

            if (!hasLevel) {
                setResult({ key: requestKey, data: null, error: null, pending: true });
                return;
            }
            const [nivelPotCurva, curva, progresionJacques] = getPotentialTalentArray(
                age_at_reply, level + sublevel, tr, curvePickForReply(created_date),
            );

            const matchKey = posicion ? {
                [PROMOTE_SOON]: 'cap_equal_role',
                [PROMOTE_NOW]: 'cap_higher_role',
                [KEEP_ROLE]: 'cap_less_role',
            }[getPositionMatch(posicion, level, sublevel)] : null;

            const reporte = patchReport(company_data.name, getReport(level, sublevel, progresionJacques, name, tr));

            const reportData = {
                nombre: toTitleCase(name),
                edad: age,
                posicion: getText(posicion, 'name', language),
                areafunc: getText(areafunc, 'name', language),
                fecha_test: dayjs(created_date).format('D/M/YYYY'),
                currentCapacity: capacityToText(level, sublevel, tr),
                potentialCapacity: nivelPotCurva ? capacityToText(nivelPotCurva.charAt(0), nivelPotCurva.charAt(1), tr) : '',
                match_persona_rol: matchKey ? tr(matchKey) : '',
                futurePotential: tr(
                    getFuturePotentialCategory(
                        nivelPotCurva, curva,
                        company_data.potential_ruleset || DEFAULT_POTENTIAL_RULESET,
                    ).toLowerCase(),
                ),
                reporte,
                company: {
                    id: company_data.id,
                    name: company_data.name,
                    logofile: company_data.logofile,
                    chart: company_data.chart,
                },
                curva,
                age_at_reply,
            };

            setResult({
                key: requestKey, data: reportData, error: null, pending: false,
            });
        }).catch((err) => {
            if (err?.name === 'AbortError') return;
            console.error('Error al cargar reporte:', err);
            setResult({
                key: requestKey, data: null, error: err, pending: false,
            });
        });
        return () => controller.abort();
    }, [id, type, language, requestKey]);

    const { data, error, pending } = result;
    const loading = Boolean(id) && result.key !== requestKey;

    return { data, loading, error, pending };
}
